// TelcoTrack — per-user visibility of stavby (polygon allowlist, not BUDOVA).
// Default is ALL. `seeAllStavby: false` + IDs = allowlist. Missing claims (old JWT) = ALL.
// Scope is the whole stavba: polygony, finance, navolávání, Gantt, materiál, výkaz,
// dokumenty, úřady, objekty — not the polygons list alone.

export interface UserStavbyVisibility {
  seeAllStavby: boolean;
  allowedPolygonIds: string[];
}

export interface StavbyPickerRow {
  id: string;
  name?: string | null;
  kiaCode?: string | null;
  realizationYear?: string | null;
  responsibleBranch?: string | null;
  /** Firma (tenant company) — stavby jiných firem stejného klienta lze přiřadit přes allowlist. */
  companyId?: string | null;
  companyName?: string | null;
}

export const DEFAULT_USER_STAVBY_VISIBILITY: UserStavbyVisibility = {
  seeAllStavby: true,
  allowedPolygonIds: [],
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normalizeAllowedPolygonIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const ids = raw
    .map((value) => String(value ?? '').trim())
    .filter((id) => UUID_RE.test(id));
  return [...new Set(ids)];
}

/**
 * Canonical visibility. `seeAllStavby !== false` (including missing/undefined) → ALL.
 * Empty allowlist + not-all is an explicit empty set (see nothing) — UI must not save that.
 */
export function resolveUserStavbyVisibility(input?: {
  seeAllStavby?: boolean | null;
  allowedPolygonIds?: unknown;
} | null): UserStavbyVisibility {
  if (input == null || input.seeAllStavby !== false) {
    return { seeAllStavby: true, allowedPolygonIds: [] };
  }
  return {
    seeAllStavby: false,
    allowedPolygonIds: normalizeAllowedPolygonIds(input.allowedPolygonIds),
  };
}

export function userCanSeePolygon(
  visibility: UserStavbyVisibility,
  polygonId?: string | null,
): boolean {
  if (visibility.seeAllStavby) return true;
  if (!polygonId) return false;
  return visibility.allowedPolygonIds.includes(polygonId);
}

/** `null` = no restriction. `[]` = see nothing. */
export function allowedPolygonIdsOrNull(visibility: UserStavbyVisibility): string[] | null {
  if (visibility.seeAllStavby) return null;
  return visibility.allowedPolygonIds;
}

export const STAVBY_ACCESS_DENIED_MESSAGE = 'Nemáte přístup k této stavbě.';

/**
 * Resolve list/query scope. `denied` = caller asked for polygon(s) outside the allowlist.
 * `polygonIds: null` = no SQL restriction (see all). `[]` = see nothing (empty lists).
 */
export function resolveScopedPolygonIds(
  visibility: UserStavbyVisibility,
  requested?: string[] | null,
): { polygonIds: string[] | null; denied: boolean } {
  if (requested?.length) {
    const scoped = scopePolygonIds(visibility, requested);
    if (!visibility.seeAllStavby && (!scoped || scoped.length === 0)) {
      return { polygonIds: [], denied: true };
    }
    return { polygonIds: scoped, denied: false };
  }
  return { polygonIds: allowedPolygonIdsOrNull(visibility), denied: false };
}

/**
 * Intersect an optional requested set with the user allowlist.
 * `null` result = no SQL restriction (see all).
 */
export function scopePolygonIds(
  visibility: UserStavbyVisibility,
  requested?: string[] | null,
): string[] | null {
  if (visibility.seeAllStavby) return requested?.length ? requested : null;
  if (!requested?.length) return visibility.allowedPolygonIds;
  const allowed = new Set(visibility.allowedPolygonIds);
  return requested.filter((id) => allowed.has(id));
}

export function filterByStavbyVisibility<T>(
  items: T[],
  visibility: UserStavbyVisibility,
  getPolygonId: (item: T) => string | null | undefined,
): T[] {
  if (visibility.seeAllStavby) return items;
  const allowed = new Set(visibility.allowedPolygonIds);
  return items.filter((item) => {
    const id = getPolygonId(item);
    return !!id && allowed.has(id);
  });
}

export function parseStavbyVisibilityFromUserInfo(userInfo: unknown): UserStavbyVisibility {
  if (!userInfo || typeof userInfo !== 'object') {
    return DEFAULT_USER_STAVBY_VISIBILITY;
  }
  const record = userInfo as Record<string, unknown>;
  return resolveUserStavbyVisibility({
    seeAllStavby: record['seeAllStavby'] as boolean | undefined,
    allowedPolygonIds: record['allowedPolygonIds'],
  });
}

/** JWT claims: omit IDs when user sees all (keeps tokens small). */
export function stavbyVisibilityJwtClaims(input?: {
  seeAllStavby?: boolean | null;
  allowedPolygonIds?: unknown;
} | null): { seeAllStavby: boolean; allowedPolygonIds?: string[] } {
  const visibility = resolveUserStavbyVisibility(input);
  if (visibility.seeAllStavby) {
    return { seeAllStavby: true };
  }
  return { seeAllStavby: false, allowedPolygonIds: visibility.allowedPolygonIds };
}

export function filterStavbyPickerRows<T extends StavbyPickerRow>(
  rows: T[],
  filters: {
    search?: string;
    branches?: string[];
    years?: string[];
    companyIds?: string[];
  },
): T[] {
  const search = (filters.search ?? '').trim().toLowerCase();
  const branches = new Set((filters.branches ?? []).map((b) => b.trim()).filter(Boolean));
  const years = new Set((filters.years ?? []).map((y) => y.trim()).filter(Boolean));
  const companyIds = new Set((filters.companyIds ?? []).map((c) => c.trim()).filter(Boolean));

  return rows.filter((row) => {
    if (companyIds.size > 0 && !companyIds.has(String(row.companyId ?? '').trim())) {
      return false;
    }
    if (branches.size > 0 && !branches.has(String(row.responsibleBranch ?? '').trim())) {
      return false;
    }
    if (years.size > 0 && !years.has(String(row.realizationYear ?? '').trim())) {
      return false;
    }
    if (!search) return true;
    const haystack = [row.name, row.kiaCode, row.realizationYear, row.responsibleBranch, row.companyName]
      .map((part) => String(part ?? '').toLowerCase())
      .join(' ');
    return haystack.includes(search);
  });
}

export function toggleStavbyPickerSelection(
  selectedIds: string[],
  id: string,
  selected: boolean,
): string[] {
  const trimmed = id.trim();
  if (!trimmed) return [...selectedIds];
  const set = new Set(selectedIds);
  if (selected) set.add(trimmed);
  else set.delete(trimmed);
  return [...set];
}

export function selectAllFilteredStavby(selectedIds: string[], filteredIds: string[]): string[] {
  const set = new Set(selectedIds);
  for (const id of filteredIds) {
    const trimmed = String(id ?? '').trim();
    if (trimmed) set.add(trimmed);
  }
  return [...set];
}

export function deselectFilteredStavby(selectedIds: string[], filteredIds: string[]): string[] {
  const remove = new Set(filteredIds.map((id) => String(id ?? '').trim()).filter(Boolean));
  return selectedIds.filter((id) => !remove.has(id));
}
